import { ProgressBar } from "primereact/progressbar";
import React from "react";
import AttachmentIcon from "~/components/Processes/conversations/AttachmentIcon";
import {
  formatFileSize,
  getDocumentLabel,
  openFileInNewTab,
  formatVersionedAttachmentDisplayName,
} from "~/utils/fileUtils";
import CategoryPicker from "~/components/Processes/conversations/CategoryPicker";
import FileYearMonthPicker from "~/components/Processes/conversations/FileYearMonthPicker";
import { Button } from "primereact/button";
import TaskCommentError from "./TaskCommentError";
import PropTypes from "prop-types";

const TaskAttachmentList = ({
  files,
  onFileRemove,
  isLoading,
  handleSelectCategory,
  handleSelectFileDate,
  categories = [],
  isPayslipFile = false,
  tryAgainHandler,
}) => {
  const normalizedFiles = files.map((x, i) => {
    const fileObj = x.file;
    const period =
      fileObj?.fileYearMonth?.year && fileObj?.fileYearMonth?.month
        ? `${fileObj.fileYearMonth.year}-${String(
            fileObj.fileYearMonth.month,
          ).padStart(2, "0")}`
        : null;

    return {
      ...fileObj,
      name: fileObj?.fileName || fileObj?.name,
      category: fileObj?.category,
      subCategory: fileObj?.subCategory,
      period,
    };
  });

  const getVersionKey = (nf) => {
    const subCode =
      typeof nf?.subCategory === "object"
        ? nf.subCategory?.code
        : nf?.subCategory;
    return `${nf?.category ?? ""}_${subCode ?? ""}_${nf?.period ?? ""}`;
  };

  const versionNumberByIndex = normalizedFiles.map((nf, i) => {
    const key = getVersionKey(nf);
    const sameKeyIndices = normalizedFiles
      .map((f, idx) => (getVersionKey(f) === key ? idx : null))
      .filter((idx) => idx !== null);
    return sameKeyIndices.indexOf(i) + 1;
  });

  const getSubCategoryName = (categoryCode, subCategoryCode) => {
    const cat = categories?.find((c) => c.code === categoryCode);
    const sub = cat?.subCategory?.find((s) => s.code === subCategoryCode);
    return sub?.name ?? subCategoryCode ?? "-";
  };

  return (
    <div>
      {files.length ? <p className="heading">Files Uploaded</p> : null}
      <div
        className={`attachment__list ${files.length > 2 ? "list__overflow" : ""}`}
      >
        {files.map((data, index) => {
          const f = data?.file;
          const normalized = normalizedFiles[index] || {};
          let displayFileName = f?.fileName || f?.name || "";

          if (!isPayslipFile && f) {
            const categoryCode = f?.category;
            const subCategoryCode =
              typeof f?.subCategory === "object"
                ? f.subCategory?.code
                : f?.subCategory;

            if (categoryCode && subCategoryCode) {
              const subCategoryName = getSubCategoryName(
                categoryCode,
                subCategoryCode,
              );
              const periodMonthYear =
                f?.fileYearMonth?.year && f?.fileYearMonth?.month
                  ? new Date(
                      f.fileYearMonth.year,
                      f.fileYearMonth.month - 1,
                      1,
                    ).toLocaleString("default", {
                      month: "long",
                      year: "numeric",
                    })
                  : null;
              const versionNumber =
                index >= 0 ? (versionNumberByIndex[index] ?? 1) : 1;
              const extension = (f?.fileName || f?.name || "")
                .split(".")
                .pop()
                .toLowerCase();

              displayFileName = formatVersionedAttachmentDisplayName(
                subCategoryName || "File",
                periodMonthYear,
                versionNumber,
                extension,
              );
            }
          }

          const displaySize =
            typeof data?.file?.size === "number" ? data.file.size : 0;
          return (
            <React.Fragment key={index}>
              <div className="w-full attachment__card">
                <div className="attachment__card-green-box">
                  <div className="attachment__card-inner flex align-items-center justify-content-between">
                    <div className="flex align-items-center">
                      <span className="attachment__card--icon">
                        <AttachmentIcon file={data?.file} />
                      </span>
                      <div className="w-full">
                        <div className="attahment__file__detail">
                          <p onClick={() => openFileInNewTab(data?.file)}>
                            {displayFileName}
                          </p>
                          <p>
                            {getDocumentLabel(displayFileName)}. File Size:{" "}
                            {formatFileSize(displaySize)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex align-items-center gap-2">
                      <Button
                        icon="pi pi-external-link"
                        className="attachment-action-btn"
                        outlined
                        onClick={() => openFileInNewTab(data?.file)}
                        aria-label="Preview"
                      />
                      <Button
                        icon="pi pi-trash"
                        className="attachment-action-btn"
                        outlined
                        onClick={() => onFileRemove(data)}
                        aria-label="Delete"
                      />
                    </div>
                  </div>
                  <div className="task-attachment-meta-row flex align-items-center">
                    {isPayslipFile ? (
                      <div className="payslip-input">
                        <p>Payslip</p>
                        <i className="pi pi-lock" />
                      </div>
                    ) : (
                      <div className="task-attachment-controls-box">
                        <div className="task-attachment-category-field">
                          <CategoryPicker
                            categories={categories}
                            value={data.code}
                            onChange={(e) => handleSelectCategory(e, index)}
                            placeholder="Select a Category"
                            portalToBody
                            forceDownward
                          />
                        </div>
                        {handleSelectFileDate && (
                          <FileYearMonthPicker
                            value={
                              data?.file?.fileYearMonth?.year &&
                              data?.file?.fileYearMonth?.month
                                ? new Date(
                                    data.file.fileYearMonth.year,
                                    data.file.fileYearMonth.month - 1,
                                    1,
                                  )
                                : null
                            }
                            onChange={(date) => {
                              if (date) {
                                handleSelectFileDate(
                                  date.getFullYear(),
                                  date.getMonth() + 1,
                                  index,
                                );
                              }
                            }}
                            placeholder="Select File Date"
                            buttonClassName="task-attachment-date-btn"
                            containerClassName="task-attachment-file-year-month-field"
                            labelClassName="task-attachment-date-label"
                            placeholderClassName="task-attachment-date-placeholder"
                            portalToBody
                            forceDownward
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {isLoading && (
                  <ProgressBar
                    value={40}
                    mode="indeterminate"
                    color="#188A42"
                    showValue={false}
                  ></ProgressBar>
                )}
              </div>

              {data &&
                data.error &&
                Object.keys(data.error).length > 1 &&
                !isLoading && (
                  <TaskCommentError
                    summary={data.error.summary}
                    detail={data.error.detail}
                    otherAction={data.error?.otherAction}
                    handleOtherAction={() => tryAgainHandler(data)}
                  />
                )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

TaskAttachmentList.propTypes = {
  files: PropTypes.array,
  onFileRemove: PropTypes.func,
  isLoading: PropTypes.bool,
  handleSelectCategory: PropTypes.func,
  handleSelectFileDate: PropTypes.func,
  categories: PropTypes.array,
  isPayslipFile: PropTypes.bool,
  tryAgainHandler: PropTypes.func,
};

export default TaskAttachmentList;
